<script setup>
import { ref, onMounted, watch } from 'vue';
import { Pencil, Square, ArrowUpRight, Undo2 } from 'lucide-vue-next';

const props = defineProps({
  image: { type: String, default: '' }, // dataURL du screenshot
});

const canvasRef = ref(null);
const tool = ref('pen'); // 'pen' | 'rect' | 'arrow'
const COLOR = '#ef4444';
let ctx = null;
let img = null;
let drawing = false;
let startX = 0;
let startY = 0;
const history = []; // pile d'ImageData pour l'undo (etat AVANT chaque trace)

function redrawBase() {
  if (!ctx || !img) return;
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.drawImage(img, 0, 0);
}

function fitCanvas() {
  const canvas = canvasRef.value;
  if (!canvas || !img) return;
  canvas.width = img.naturalWidth || 800;
  canvas.height = img.naturalHeight || 600;
  ctx = canvas.getContext('2d');
  history.length = 0;
  redrawBase();
}

function loadImage() {
  if (!props.image) { img = null; return; }
  img = new Image();
  img.onload = fitCanvas;
  img.src = props.image;
}

onMounted(loadImage);
watch(() => props.image, loadImage);

function pointerPos(e) {
  const canvas = canvasRef.value;
  const rect = canvas.getBoundingClientRect();
  return {
    x: (e.clientX - rect.left) * (canvas.width / rect.width),
    y: (e.clientY - rect.top) * (canvas.height / rect.height),
  };
}

function onDown(e) {
  if (!ctx) return;
  drawing = true;
  const p = pointerPos(e);
  startX = p.x;
  startY = p.y;
  history.push(ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height));
  if (history.length > 15) history.shift(); // borne la pile undo (memoire)
  ctx.strokeStyle = COLOR;
  ctx.fillStyle = COLOR;
  ctx.lineWidth = Math.max(3, ctx.canvas.width / 400);
  ctx.lineCap = 'round';
  if (tool.value === 'pen') {
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }
  try { canvasRef.value.setPointerCapture(e.pointerId); } catch { /* ignore */ }
}

function onMove(e) {
  if (!drawing || !ctx) return;
  const p = pointerPos(e);
  if (tool.value === 'pen') {
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  } else {
    ctx.putImageData(history[history.length - 1], 0, 0); // previsualisation
    if (tool.value === 'rect') {
      ctx.strokeRect(startX, startY, p.x - startX, p.y - startY);
    } else {
      drawArrow(startX, startY, p.x, p.y);
    }
  }
}

function onUp(e) {
  drawing = false;
  if (e && e.pointerId != null && canvasRef.value) {
    try { canvasRef.value.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  }
}

function drawArrow(x1, y1, x2, y2) {
  const head = Math.max(10, ctx.canvas.width / 80);
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
}

function undo() {
  if (!ctx || history.length === 0) return;
  ctx.putImageData(history.pop(), 0, 0);
}

function exportDataUrl() {
  if (!canvasRef.value || !img) return props.image;
  return canvasRef.value.toDataURL('image/jpeg', 0.8);
}

defineExpose({ exportDataUrl });
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <button type="button" :class="['p-2 rounded-lg', tool === 'pen' ? 'bg-[#e94591] text-white' : 'bg-gray-100']" title="Trait libre" @click="tool = 'pen'"><Pencil class="w-4 h-4" /></button>
      <button type="button" :class="['p-2 rounded-lg', tool === 'rect' ? 'bg-[#e94591] text-white' : 'bg-gray-100']" title="Rectangle" @click="tool = 'rect'"><Square class="w-4 h-4" /></button>
      <button type="button" :class="['p-2 rounded-lg', tool === 'arrow' ? 'bg-[#e94591] text-white' : 'bg-gray-100']" title="Flèche" @click="tool = 'arrow'"><ArrowUpRight class="w-4 h-4" /></button>
      <button type="button" class="p-2 rounded-lg bg-gray-100 ml-auto" title="Annuler" @click="undo"><Undo2 class="w-4 h-4" /></button>
    </div>
    <div class="border border-gray-200 rounded-lg overflow-auto max-h-[55vh] bg-gray-50">
      <canvas
        v-if="image"
        ref="canvasRef"
        class="max-w-full touch-none cursor-crosshair block"
        @pointerdown="onDown"
        @pointermove="onMove"
        @pointerup="onUp"
        @pointercancel="onUp"
        @pointerleave="onUp"
      />
      <p v-else class="text-xs text-gray-400 p-6 text-center">La capture d'écran a échoué sur cette page.<br />Tu peux quand même décrire le bug et envoyer le rapport.</p>
    </div>
  </div>
</template>
